import { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api, agentAvatarRawSrc, type Agent, type PipelineFlow, type ClaudeModel, type AgentDbGrant, type ProjectEnvironment } from '../api';
import { Page, Card, Button, Field, Input, Textarea, Select, GenAvatar, Markdown } from '../ui';

const FLOWS: PipelineFlow[] = ['suggestion', 'bug', 'feature'];
const FLOW_LABEL: Record<PipelineFlow, string> = { suggestion: 'Sugestão', bug: 'Bug', feature: 'Feature' };

/** Select de modelo do Claude Code (lista curada + "padrão" + o valor atual se for custom). */
function ModelSelect({ value, models, onChange }: { value: string; models: ClaudeModel[]; onChange: (v: string) => void }) {
  const known = models.some((m) => m.id === value);
  return (
    <Select value={value} onChange={(e) => onChange(e.target.value)}>
      <option value="">padrão do sistema</option>
      {models.map((m) => (
        <option key={m.id} value={m.id}>{m.label} ({m.tier})</option>
      ))}
      {value && !known && <option value={value}>{value} (custom)</option>}
    </Select>
  );
}

export function AgentEditor() {
  const { slug, agentId } = useParams<{ slug: string; agentId: string }>();
  const nav = useNavigate();
  const fileRef = useRef<HTMLInputElement>(null);

  const [agent, setAgent] = useState<Agent | null>(null);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [model, setModel] = useState('');
  const [engine, setEngine] = useState<'claude' | 'hermes'>('claude');
  const [writes, setWrites] = useState('');
  const [hermesOn, setHermesOn] = useState(false);
  const [enabled, setEnabled] = useState(true);
  const [soul, setSoul] = useState('');
  const [soulPreview, setSoulPreview] = useState(false);
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  const [greeting, setGreeting] = useState('');
  const [accentColor, setAccentColor] = useState('');

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [avatarBusy, setAvatarBusy] = useState(false);

  // Modelos disponíveis (Claude Code)
  const [models, setModels] = useState<ClaudeModel[]>([]);

  // Instruções + modelo por fluxo (só pra agentes de pipeline)
  const [flowInstr, setFlowInstr] = useState<Record<PipelineFlow, { instructions: string; model: string }>>({
    suggestion: { instructions: '', model: '' },
    bug: { instructions: '', model: '' },
    feature: { instructions: '', model: '' },
  });
  const [selFlow, setSelFlow] = useState<PipelineFlow>('suggestion');
  const [savingFlow, setSavingFlow] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (!slug || !agentId) return;
    setLoading(true);
    api
      .getAgent(slug, agentId)
      .then(({ agent, soul, writes: wr }) => {
        setAgent(agent);
        setName(agent.name);
        setDescription(agent.description);
        setModel(agent.model ?? '');
        setEngine(agent.engine ?? 'claude');
        setWrites((wr ?? []).join('\n'));
        setEnabled(agent.enabled);
        setGreeting(agent.greeting ?? '');
        setAccentColor(agent.accentColor ?? '');
        setSoul(soul);
        if (agent.avatarKey) setAvatarUrl(agentAvatarRawSrc(slug, agentId, agent.avatarKey));
        api.listModels().then(({ models }) => setModels(models)).catch(() => undefined);
        if (agent.pipelineRole) {
          api.getFlowInstructions(slug, agentId).then(({ flowInstructions }) => {
            setFlowInstr((prev) => {
              const next = { ...prev };
              for (const fi of flowInstructions) next[fi.flow] = { instructions: fi.instructions, model: fi.model ?? '' };
              return next;
            });
          }).catch(() => undefined);
        }
      })
      .catch(() => setErr('Falha ao carregar agente'))
      .finally(() => setLoading(false));
    api.authProviders().then((p) => setHermesOn(p.hermes)).catch(() => undefined);
  }, [slug, agentId]);

  async function save() {
    if (!slug || !agentId || saving) return;
    setSaving(true);
    setErr(null);
    setMsg(null);
    try {
      const { agent } = await api.updateAgent(slug, agentId, {
        name: name.trim(),
        description: description.trim(),
        model: model.trim() || null,
        engine,
        writes: writes.split(/[\n,]/).map((w) => w.trim()).filter(Boolean),
        enabled,
        greeting: greeting.trim() || null,
        accentColor: accentColor.trim() || null,
        soul,
      });
      setAgent(agent);
      setMsg('Salvo.');
    } catch {
      setErr('Falha ao salvar');
    } finally {
      setSaving(false);
    }
  }

  async function saveFlow() {
    if (!slug || !agentId || savingFlow) return;
    setSavingFlow(true);
    setMsg(null);
    setErr(null);
    try {
      await api.saveFlowInstruction(slug, agentId, selFlow, {
        instructions: flowInstr[selFlow].instructions,
        model: flowInstr[selFlow].model || null,
      });
      setMsg(`Fluxo "${FLOW_LABEL[selFlow]}" salvo.`);
    } catch {
      setErr('Falha ao salvar instruções do fluxo.');
    } finally {
      setSavingFlow(false);
    }
  }

  async function generate() {
    if (!slug || !agentId || generating) return;
    if (!name.trim() || !description.trim()) {
      setErr('Preencha nome e descrição antes de gerar.');
      return;
    }
    setGenerating(true);
    setErr(null);
    setMsg('A IA está escrevendo as instruções…');
    try {
      const { soul } = await api.generateSoul(slug, agentId, { name: name.trim(), description: description.trim() });
      setSoul(soul);
      setMsg('Instruções geradas. Revise e salve.');
    } catch (e) {
      const m = (e as { error?: string }).error;
      setErr(m === 'generation_failed' ? 'A geração falhou. Tente de novo.' : 'Falha ao gerar instruções');
      setMsg(null);
    } finally {
      setGenerating(false);
    }
  }

  async function generateAvatar() {
    if (!slug || !agentId || avatarBusy) return;
    setAvatarBusy(true);
    setErr(null);
    setMsg('A IA está desenhando o avatar…');
    try {
      await api.generateAgentAvatar(slug, agentId);
      // cache-buster (timestamp) pra forçar o reload da imagem nova.
      setAvatarUrl(agentAvatarRawSrc(slug, agentId, String(Date.now())));
      setMsg('Avatar gerado.');
    } catch (e) {
      const m = (e as { error?: string }).error;
      setErr(m === 'no_svg' ? 'A IA não produziu um SVG válido. Tente de novo.' : 'Falha ao gerar avatar');
      setMsg(null);
    } finally {
      setAvatarBusy(false);
    }
  }

  async function onAvatarPick(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file || !slug || !agentId) return;
    setErr(null);
    try {
      const { agent } = await api.uploadAgentAvatar(slug, agentId, file);
      setAgent(agent);
      setAvatarUrl(agentAvatarRawSrc(slug, agentId, String(Date.now())));
    } catch {
      setErr('Falha no upload do avatar');
    }
  }

  async function remove() {
    if (!slug || !agentId) return;
    if (!confirm('Apagar este agente? O soul.md será removido do workspace.')) return;
    try {
      await api.deleteAgent(slug, agentId);
      nav(`/p/${slug}/agents`);
    } catch {
      setErr('Falha ao apagar');
    }
  }

  if (loading) return <div className="center">Carregando…</div>;
  if (!agent) return <div className="center">{err ?? 'não encontrado'}</div>;

  return (
    <Page
      title={`Agente — ${agent.name}`}
      subtitle={agent.isRouter ? 'Recepcionista: encaminha mensagens para os especialistas.' : agent.slug}
      actions={
        <>
          <Button variant="ghost" onClick={() => nav(`/p/${slug}/agents`)}>
            voltar
          </Button>
          <Button variant="danger" onClick={remove} disabled={agent.seed}>
            apagar
          </Button>
          <Button variant="primary" onClick={save} disabled={saving}>
            {saving ? 'salvando…' : 'salvar'}
          </Button>
        </>
      }
    >
      {err && <div className="chat-error">{err}</div>}
      {msg && <div className="agent-editor-msg">{msg}</div>}

      <div className="agent-editor-grid">
        <Card className="agent-editor-meta">
          <div className="agent-editor-avatar-row">
            {avatarUrl ? (
              <img className="agent-avatar agent-avatar-lg" src={avatarUrl} alt={agent.name} />
            ) : (
              <GenAvatar name={name || agent.name} size={72} />
            )}
            <div className="agent-editor-avatar-actions">
              <input ref={fileRef} type="file" accept="image/*" hidden onChange={onAvatarPick} />
              <Button variant="secondary" size="sm" onClick={() => fileRef.current?.click()} disabled={avatarBusy}>
                trocar foto
              </Button>
              <Button variant="ghost" size="sm" onClick={generateAvatar} disabled={avatarBusy}>
                {avatarBusy ? 'desenhando…' : '✨ gerar com IA'}
              </Button>
            </div>
          </div>

          <Field label="Nome">
            <Input value={name} onChange={(e) => setName(e.target.value)} />
          </Field>
          <Field label="O que ele faz" hint="Alimenta o recepcionista.">
            <Textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={3} />
          </Field>
          {(hermesOn || engine === 'hermes') && (
            <Field label="Motor" hint="Hermes: marketing/conteúdo (roda no Hermes). Claude Code: tarefas de código.">
              <Select value={engine} onChange={(e) => setEngine(e.target.value as 'claude' | 'hermes')}>
                <option value="hermes">Hermes (conteúdo / marketing)</option>
                <option value="claude">Claude Code (código)</option>
              </Select>
            </Field>
          )}
          <Field label="Pode escrever (pastas)" hint="Uma pasta/glob por linha. Ex: docs/notas/**  ·  docs/cards-beta/**. Vazio = docs/** (tudo).">
            <Textarea value={writes} onChange={(e) => setWrites(e.target.value)} rows={2} placeholder={'docs/notas/**\ndocs/cards-beta/**'} />
          </Field>
          <Field label="Modelo (Claude Code)" hint="Vazio = padrão do sistema. Controle de custo: Haiku é mais barato.">
            <ModelSelect value={model} models={models} onChange={setModel} />
          </Field>
          <Field label="Saudação na conversa" hint="Mensagem de boas-vindas exibida ao abrir o chat com este agente.">
            <Textarea value={greeting} onChange={(e) => setGreeting(e.target.value)} rows={2} placeholder={`Oi! Sou ${name || 'o agente'}. Como posso ajudar?`} />
          </Field>
          <Field label="Cor de destaque" hint="Personaliza o chat deste agente.">
            <div className="agent-accent-row">
              <input type="color" value={accentColor || '#6366f1'} onChange={(e) => setAccentColor(e.target.value)} />
              <Input value={accentColor} onChange={(e) => setAccentColor(e.target.value)} placeholder="#6366f1 (vazio = padrão)" style={{ maxWidth: 160 }} />
              {accentColor && <Button variant="ghost" size="sm" onClick={() => setAccentColor('')}>limpar</Button>}
            </div>
          </Field>
          <Field>
            <label className="agent-editor-toggle">
              <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
              <span>Ativo (aparece no chat e no roteamento)</span>
            </label>
          </Field>
        </Card>

        <Card className="agent-editor-soul">
          <div className="agent-editor-soul-head">
            <div>
              <strong>Instruções (soul.md)</strong>
              <div className="agent-editor-soul-hint">
                Markdown com front-matter (reads/writes). Versionado no workspace git.
              </div>
            </div>
            <div className="agent-soul-head-actions">
              <Button variant="ghost" size="sm" onClick={() => setSoulPreview((p) => !p)}>
                {soulPreview ? 'editar' : 'preview'}
              </Button>
              <Button variant="secondary" onClick={generate} disabled={generating}>
                {generating ? 'gerando…' : '✨ gerar com IA'}
              </Button>
            </div>
          </div>
          {soulPreview ? (
            <div className="agent-soul-preview"><Markdown text={soul} /></div>
          ) : (
            <Textarea
              className="agent-soul-textarea"
              value={soul}
              onChange={(e) => setSoul(e.target.value)}
              rows={24}
              spellCheck={false}
            />
          )}
        </Card>
      </div>

      {agent.pipelineRole && (
        <Card style={{ marginTop: 16 }}>
          <div className="card-title-row">
            <strong>Instruções por fluxo</strong>
            <span className="users-muted">papel no pipeline: {agent.pipelineRole}</span>
          </div>
          <div className="agent-flow-row">
            <Field label="Fluxo">
              <Select value={selFlow} onChange={(e) => setSelFlow(e.target.value as PipelineFlow)}>
                {FLOWS.map((f) => (
                  <option key={f} value={f}>{FLOW_LABEL[f]}</option>
                ))}
              </Select>
            </Field>
            <Field label="Modelo neste fluxo" hint="Override do modelo do agente — controle de custo.">
              <ModelSelect
                value={flowInstr[selFlow].model}
                models={models}
                onChange={(v) => setFlowInstr((p) => ({ ...p, [selFlow]: { ...p[selFlow], model: v } }))}
              />
            </Field>
            <Button variant="primary" onClick={saveFlow} disabled={savingFlow}>
              {savingFlow ? 'salvando…' : 'salvar fluxo'}
            </Button>
          </div>
          <Textarea
            rows={8}
            placeholder={`Instruções extras deste agente especificamente no fluxo "${FLOW_LABEL[selFlow]}" (complementa o soul.md).`}
            value={flowInstr[selFlow].instructions}
            onChange={(e) => setFlowInstr((p) => ({ ...p, [selFlow]: { ...p[selFlow], instructions: e.target.value } }))}
          />
        </Card>
      )}

      {slug && <DbGrantsCard slug={slug} agentId={agentId!} />}
    </Page>
  );
}

const ALL_OPS = ['select', 'insert', 'update', 'delete'] as const;

/** Acesso a dados por agente: grants (operações + tabelas) por ambiente. */
function DbGrantsCard({ slug, agentId }: { slug: string; agentId: string }) {
  const [envs, setEnvs] = useState<ProjectEnvironment[]>([]);
  const [grants, setGrants] = useState<Record<string, AgentDbGrant>>({});
  const [loading, setLoading] = useState(true);
  const [savingEnv, setSavingEnv] = useState<string | null>(null);

  useEffect(() => {
    api.listAgentDbGrants(slug, agentId)
      .then(({ environments, grants }) => {
        setEnvs(environments);
        const map: Record<string, AgentDbGrant> = {};
        for (const g of grants) map[g.environmentId] = g;
        setGrants(map);
      })
      .catch(() => undefined)
      .finally(() => setLoading(false));
  }, [slug, agentId]);

  function grantFor(envId: string): AgentDbGrant {
    return grants[envId] ?? { agentId, environmentId: envId, operations: [], tables: [], maxRows: 1000, requireConfirmWrite: true, instructions: '', enabled: false };
  }
  function update(envId: string, patch: Partial<AgentDbGrant>) {
    setGrants((g) => ({ ...g, [envId]: { ...grantFor(envId), ...patch } }));
  }
  function toggleOp(envId: string, op: string) {
    const g = grantFor(envId);
    update(envId, { operations: g.operations.includes(op) ? g.operations.filter((o) => o !== op) : [...g.operations, op] });
  }
  async function save(envId: string) {
    const g = grantFor(envId);
    setSavingEnv(envId);
    try {
      await api.saveAgentDbGrant(slug, agentId, envId, {
        operations: g.operations, tables: g.tables, maxRows: g.maxRows,
        requireConfirmWrite: g.requireConfirmWrite, instructions: g.instructions ?? '', enabled: g.enabled,
      });
    } finally {
      setSavingEnv(null);
    }
  }

  if (loading) return null;
  return (
    <Card style={{ marginTop: 16 }}>
      <div className="card-title-row">
        <strong>Acesso a dados (por ambiente)</strong>
        <span className="users-muted">o agente só acessa o que estiver liberado aqui</span>
      </div>
      {envs.length === 0 ? (
        <div className="users-muted">Nenhum ambiente cadastrado. Configure em <strong>Ambientes</strong> (container/banco/roles) primeiro.</div>
      ) : (
        <div className="dbgrants">
          {envs.map((env) => {
            const g = grantFor(env.id);
            return (
              <div key={env.id} className="dbgrant">
                <div className="dbgrant-head">
                  <label className="agent-editor-toggle">
                    <input type="checkbox" checked={g.enabled} onChange={(e) => update(env.id, { enabled: e.target.checked })} />
                    <strong>{env.name}</strong> <span className="users-muted">{env.tier}{env.dbName ? ` · ${env.dbName}` : ' · (sem banco)'}</span>
                  </label>
                  <Button size="sm" variant="primary" onClick={() => save(env.id)} disabled={savingEnv === env.id}>{savingEnv === env.id ? 'salvando…' : 'salvar'}</Button>
                </div>
                {g.enabled && (
                  <div className="dbgrant-body">
                    <div className="dbgrant-ops">
                      {ALL_OPS.map((op) => (
                        <label key={op} className="dbgrant-op">
                          <input type="checkbox" checked={g.operations.includes(op)} onChange={() => toggleOp(env.id, op)} /> {op}
                        </label>
                      ))}
                    </div>
                    <Field label="Tabelas permitidas (vírgula; * = todas)">
                      <Input value={g.tables.join(', ')} onChange={(e) => update(env.id, { tables: e.target.value.split(',').map((s) => s.trim()).filter(Boolean) })} placeholder="users, orders" />
                    </Field>
                    <div className="dbgrant-row2">
                      <Field label="Máx. linhas"><Input type="number" value={String(g.maxRows)} onChange={(e) => update(env.id, { maxRows: Number(e.target.value) || 1000 })} style={{ width: 110 }} /></Field>
                      <label className="agent-editor-toggle"><input type="checkbox" checked={g.requireConfirmWrite} onChange={(e) => update(env.id, { requireConfirmWrite: e.target.checked })} /> exigir confirmação p/ escrita</label>
                    </div>
                    <Field label="Instruções (opcional)"><Input value={g.instructions ?? ''} onChange={(e) => update(env.id, { instructions: e.target.value })} placeholder="ex: só consulte X; nunca toque em Y" /></Field>
                    {env.tier === 'prod' && g.operations.some((o) => o !== 'select') && <div className="chat-error">⚠️ Escrita em PROD — confirmação será sempre exigida.</div>}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
}
